import markerSDK, { type MarkerReporter } from "@marker.io/browser";
import * as Sentry from "@sentry/react";

const LOAD_TIMEOUT_MS = 10_000;
const SEND_WATCHDOG_MS = 30_000;
const MARKER_PROJECT_ID = "68c19f0805a0955e0d406b71";

class MarkerIOLoadTimeoutError extends Error {}

type MarkerWidget = Awaited<ReturnType<typeof markerSDK.loadWidget>>;

let markerWidget: MarkerWidget | null = null;
let initPromise: Promise<MarkerWidget | null> | null = null;
// The reporter is set from the auth handler, which can run before the widget
// has finished loading (or before it is loaded at all, when the first report
// is triggered lazily from the Help menu). Keep it until a widget exists.
let pendingReporter: MarkerReporter | null = null;

const instrumentSendLifecycle = (widget: MarkerWidget) => {
  let sendWatchdog: ReturnType<typeof setTimeout> | null = null;

  const clearWatchdog = () => {
    if (sendWatchdog) {
      clearTimeout(sendWatchdog);
      sendWatchdog = null;
    }
  };

  widget.on("feedbackbeforesend", () => {
    if (sendWatchdog) {
      Sentry.captureMessage(
        "marker.io feedbackbeforesend fired while previous send still pending",
        "warning",
      );
    }
    clearWatchdog();
    sendWatchdog = setTimeout(() => {
      Sentry.captureMessage(
        "marker.io send appears hung (no sent/error in 30s)",
        "warning",
      );
      sendWatchdog = null;
    }, SEND_WATCHDOG_MS);
  });

  widget.on("feedbacksent", clearWatchdog);

  widget.on("feedbackerror", () => {
    clearWatchdog();
    Sentry.captureMessage("marker.io feedbackerror fired", "warning");
  });
};

const loadWithTimeout = async (): Promise<MarkerWidget | null> => {
  let loadTimeoutId: ReturnType<typeof setTimeout> | undefined;
  const loadPromise = markerSDK.loadWidget({ project: MARKER_PROJECT_ID });

  try {
    markerWidget = await Promise.race([
      loadPromise,
      new Promise<never>((_, reject) => {
        loadTimeoutId = setTimeout(
          () => reject(new MarkerIOLoadTimeoutError()),
          LOAD_TIMEOUT_MS,
        );
      }),
    ]);
    clearTimeout(loadTimeoutId);
    instrumentSendLifecycle(markerWidget);
    // No floating launcher: reports are triggered from the Help menu instead.
    markerWidget.hide();
    if (pendingReporter) {
      markerWidget.setReporter(pendingReporter);
    }
    return markerWidget;
  } catch (error) {
    clearTimeout(loadTimeoutId);
    loadPromise.then((widget) => widget.unload()).catch(() => {});
    initPromise = null;
    if (error instanceof MarkerIOLoadTimeoutError) {
      Sentry.captureMessage("marker.io load timed out", "warning");
    } else {
      const isError = error instanceof Error;
      Sentry.captureMessage("marker.io load failed", {
        level: "warning",
        extra: {
          errorName: isError ? error.constructor.name : typeof error,
          errorMessage: isError ? error.message : String(error),
        },
      });
    }
    console.error("Failed to initialize Marker.io:", error);
    return null;
  }
};

export const initializeMarkerIO = async () => {
  if (markerWidget) return markerWidget;
  if (!initPromise) {
    initPromise = loadWithTimeout();
  }
  return initPromise;
};

export const setMarkerReporter = (email: string, fullName: string) => {
  pendingReporter = { email, fullName };
  markerWidget?.setReporter(pendingReporter);
};

export const clearMarkerReporter = () => {
  pendingReporter = null;
  markerWidget?.setReporter({
    email: "",
    fullName: "",
  });
};

// Opens the Marker.io capture flow, loading the widget first if the app-start
// preload never ran or failed. Returns false when the widget is unavailable so
// the caller can offer a fallback.
export const captureMarkerIOReport = async () => {
  const widget = await initializeMarkerIO();
  if (!widget) return false;

  try {
    await widget.capture("fullscreen");
    return true;
  } catch (error) {
    Sentry.captureMessage("marker.io capture failed", {
      level: "warning",
      extra: {
        errorMessage: error instanceof Error ? error.message : String(error),
      },
    });
    return false;
  }
};
